import styled from 'styled-components';
import { LauncherStatesData } from '../../../hooks/stores/launcherStatesStore';
import { UIData, useUIStore } from '../../../hooks/stores/uiStore';
import { useScene } from '../../../context/sceneContext';

export const VisibilityButton = ({
  forState,
  launcherStates,
  onlyDirectVisibility,
  noUndefined,
  computedVisibility,
}: {
  forState?: [keyof LauncherStatesData | null, string | null];
  launcherStates?: LauncherStatesData;
  onlyDirectVisibility?: boolean;
  noUndefined?: boolean;
  computedVisibility?: boolean;
}) => {
  const scene = useScene();
  const [UIData] = useUIStore<UIData>();
  const useDirectVisibility = UIData.useDirectVisibility;
  const isPopupSelected = UIData.selectedElementIDs.some((id) => {
    const { type } = scene.getElementData(id, { type: undefined }, '');
    return type === 'popup';
  });
  const isVisibilityButtonVisible = UIData.selectedElementIDs.length > 0 && !isPopupSelected;

  const directVisibility = forState ? scene.getSelectedElementsDirectVisibility(forState) : undefined;
  const finalComputedVisibility = computedVisibility !== undefined ? computedVisibility :
    onlyDirectVisibility
      ? directVisibility
      : scene.getSelectedElementsComputedVisibility(forState, launcherStates);

  const toggleVisibility = () => {
    if (finalComputedVisibility === undefined) {
      scene.setSelectedElementsVisibility(false, forState);
      return;
    }
    scene.setSelectedElementsVisibility(!finalComputedVisibility, forState);
  };

  const setVisibilityToUndefined = () => {
    if (noUndefined) return;
    scene.setSelectedElementsVisibility(undefined, forState);
  };

  return (
    <VisibleButton
      onClick={toggleVisibility}
      onContextMenu={setVisibilityToUndefined}
      $isHidden={!isVisibilityButtonVisible}
      $computedVisibility={finalComputedVisibility}
      $isVisibilityInherited={directVisibility === undefined}
      $useDirectVisibility={useDirectVisibility}
    >
      <i
        className={finalComputedVisibility !== false ? 'icon-visible' : 'icon-notvisible'}
        style={{ fontSize: '12px', lineHeight: '12px' }}
      />
    </VisibleButton>
  );
};

const VisibleButton = styled.div<{
  $isHidden?: boolean;
  $computedVisibility?: boolean;
  $isVisibilityInherited?: boolean;
  $useDirectVisibility?: boolean;
}>`
  --text-color: var(--main-2);
  --border-color: transparent;

  ${({ $computedVisibility }) =>
    $computedVisibility === false &&
    `
    --text-color: var(--error-1);
    --border-color: var(--error-1);
  `}
  ${({ $computedVisibility, $isVisibilityInherited, $useDirectVisibility }) =>
    $computedVisibility === true &&
    $isVisibilityInherited === false &&
    !$useDirectVisibility &&
    `
    --text-color: var(--primary-1);
    --border-color: var(--primary-1);
  `}

  box-sizing: border-box;
  cursor: pointer;
  width: 25px;
  height: 25px;

  display: flex;
  justify-content: center;
  align-items: center;

  background-color: var(--neutral-2);
  border: 1px solid var(--border-color);
  border-radius: 50%;

  color: var(--text-color);

  transition:
    background-color var(--transition-2),
    color var(--transition-2),
    opacity var(--transition-2),
    border var(--transition-2);
  &:hover {
    background-color: var(--neutral-3);
  }

  ${({ $isVisibilityInherited }) =>
    $isVisibilityInherited === true &&
    `
    background-color: transparent; backdrop-filter: blur(2px);
    border: 1px solid transparent;
    &:hover {
      background-color: transparent;
      border: 1px solid var(--neutral-3);
    }
  `}

  ${({ $isHidden }) => $isHidden && 'opacity: 0; pointer-events: none;'}
`;
